import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { useModal } from '../../../contexts/ModalContext';

const services = [
  {
    number: '01',
    title: 'Custom Residential Architecture',
    tagline: 'Every line drawn with purpose.',
    description:
      'Bespoke homes designed from concept to completion. We balance aesthetic vision with the reality of how you live — delivering spaces that are both beautiful and deeply functional.',
    image:
      'url(https://public.readdy.ai/ai/img_res/2e42670691173eaf375ca921c69f5ba2.jpg)',
    link: '/services',
  },
  {
    number: '02',
    title: 'Renovations & Additions',
    tagline: 'Elevate what already exists.',
    description:
      'Thoughtful expansions and transformations that respect a home\'s character while unlocking its full potential. Renovation design that feels native — never added-on.',
    image:
      'url(https://readdy.ai/api/search-image?query=luxury%20residential%20interior%20renovation%20transformation%20open%20concept%20modern%20kitchen%20marble%20countertops%20high%20end%20cabinetry%20natural%20light%20streaming%20through%20large%20windows%20double%20height%20ceiling%20warm%20wood%20accents%20award%20winning%20interior%20design%20premium%20home%20renovation%20editorial%20photography&width=1200&height=800&seq=svc-renovation-2026&orientation=landscape)',
    link: '/services',
  },
  {
    number: '03',
    title: 'New Home Design',
    tagline: 'From site to structure — complete.',
    description:
      'Full-scope architectural design for new construction. Site analysis, spatial planning, and permit-ready documentation — with attention to every element that defines a lasting home.',
    image:
      'url(https://readdy.ai/api/search-image?query=brand%20new%20luxury%20residential%20home%20exterior%20daytime%20modern%20architectural%20masterpiece%20clean%20geometric%20forms%20natural%20materials%20wood%20stone%20expansive%20panoramic%20windows%20professional%20manicured%20landscaping%20blue%20sky%20backdrop%20premium%20new%20construction%20residential%20architecture%20elegant%20minimalist%20contemporary%20design&width=1200&height=800&seq=svc-new-home-2026&orientation=landscape)',
    link: '/services',
  },
  {
    number: '04',
    title: 'Interior Spatial Planning',
    tagline: 'Space that serves how you live.',
    description:
      'Interior layout development that creates intuitive flow, maximizes natural light, and ensures each room serves its intended purpose with clarity and intention.',
    image:
      'url(https://readdy.ai/api/search-image?query=luxury%20residential%20interior%20spatial%20design%20architecture%20open%20concept%20living%20space%20soaring%20high%20ceilings%20natural%20light%20flooding%20floor%20to%20ceiling%20windows%20refined%20material%20palette%20warm%20wood%20tones%20white%20walls%20architectural%20interior%20photography%20premium%20home%20elegant%20minimalist%20contemporary%20design&width=1200&height=800&seq=svc-interior-2026&orientation=landscape)',
    link: '/services',
  },
];

export default function ServicesOverview() {
  const headerRef = useScrollReveal();
  const { openModal } = useModal();

  return (
    <section className="bg-soft-white">
      {/* Section header */}
      <div className="max-w-7xl mx-auto px-8 lg:px-16 pt-36 pb-20">
        <div ref={headerRef} className="reveal-on-scroll flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <div className="lg:w-7/12">
            <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-4">What We Design</p>
            <h2 className="font-serif text-5xl lg:text-7xl font-light text-dark-ink leading-[1.0]">
              Residential<br />Architecture Services
            </h2>
          </div>
          <div className="lg:w-5/12 flex flex-col items-start lg:items-end gap-5">
            <p className="font-sans text-sm text-warm-gray leading-relaxed max-w-xs text-left lg:text-right">
              Four distinct services — all grounded in the same belief that great architecture begins with understanding how people live.
            </p>
            <Link
              to="/services"
              className="font-sans text-[10px] tracking-[0.18em] uppercase text-dark-ink flex items-center gap-3 hover:text-wood transition-colors cursor-pointer whitespace-nowrap group"
            >
              <span>Explore All Services</span>
              <div className="w-6 h-px bg-dark-ink group-hover:w-10 group-hover:bg-wood transition-all duration-300" />
            </Link>
          </div>
        </div>
      </div>

      {/* Alternating service rows */}
      {services.map((svc, i) => (
        <ServiceRow key={i} service={svc} reverse={i % 2 !== 0} light={i % 2 === 0} />
      ))}

      {/* Mid-page CTA */}
      <MidPageCTA onCta={openModal} />
    </section>
  );
}

function ServiceRow({
  service,
  reverse,
  light,
}: {
  service: (typeof services)[0];
  reverse: boolean;
  light: boolean;
}) {
  const textRef = useScrollReveal();
  const imgRef = useScrollReveal();

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 min-h-[540px] ${light ? 'bg-soft-white' : 'bg-parchment'}`}>
      {/* Image */}
      <div
        ref={imgRef}
        className={`reveal-scale relative min-h-[360px] lg:min-h-0 overflow-hidden ${reverse ? 'lg:order-2' : ''}`}
      >
        <div
          className="absolute inset-0 bg-cover bg-top hover:scale-[1.04] transition-transform duration-1000 ease-out"
          style={{ backgroundImage: service.image }}
        />
        <div className="absolute inset-0 bg-dark-ink/10" />
        <div className="absolute bottom-6 left-6">
          <span className="font-serif text-8xl font-light text-white/15 select-none">{service.number}</span>
        </div>
      </div>

      {/* Text */}
      <div
        ref={textRef}
        className={`reveal-on-scroll flex flex-col justify-center px-10 lg:px-20 py-16 lg:py-24 ${reverse ? 'lg:order-1' : ''}`}
      >
        <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-3">{service.tagline}</p>
        <h3 className="font-serif text-4xl lg:text-5xl font-light text-dark-ink leading-[1.1] mb-6">
          {service.title}
        </h3>
        <p className="font-sans text-sm text-warm-gray leading-[1.85] mb-8 max-w-sm">
          {service.description}
        </p>
        <Link
          to={service.link}
          className="inline-flex items-center gap-3 font-sans text-[10px] tracking-[0.18em] uppercase text-dark-ink hover:text-wood transition-colors cursor-pointer group whitespace-nowrap"
        >
          <span>Learn More</span>
          <div className="w-6 h-px bg-dark-ink group-hover:w-10 group-hover:bg-wood transition-all duration-300" />
        </Link>
      </div>
    </div>
  );
}

function MidPageCTA({ onCta }: { onCta: () => void }) {
  const ref = useScrollReveal();
  return (
    <div ref={ref} className="reveal-on-scroll bg-dark-ink py-20 px-8 lg:px-16">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-8">
        <div>
          <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-3">Ready to Begin?</p>
          <h3 className="font-serif text-3xl lg:text-4xl font-light text-white">
            Let&apos;s discuss your architectural vision.
          </h3>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
          <button
            onClick={onCta}
            className="font-sans text-[10px] tracking-[0.2em] uppercase px-10 py-4 bg-white text-dark-ink hover:bg-soft-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            Schedule Consultation
          </button>
          <a
            href="tel:+19146452940"
            className="font-sans text-[10px] tracking-[0.2em] uppercase px-10 py-4 border border-white/25 text-white hover:bg-white/10 transition-colors duration-300 cursor-pointer whitespace-nowrap text-center"
          >
            (914) 645-2940
          </a>
        </div>
      </div>
    </div>
  );
}
